/* eslint-disable react-refresh/only-export-components */
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from 'urql'
import { Customer } from '../../../gql/graphql'
import { compact } from '../../_helpers/compact'
import { useSelectFestival } from '../../_hooks/useSelectFestival'
import { FESTIVAL_SELECTION_PATH } from '../../_lib/festival'
import { myFestivalsQuery } from '../../_lib/graphql/queries'
import { useMyself } from '../UserContext'
import { FestivalSelection } from '../FestivalSelection'
import { FestivalContextValue } from './FestivalContextValue'
import { FestivalLoading } from './FestivalLoading'

export { useFestival } from './useFestival'

type PropTypes = {
  children: ReactNode
}

const FestivalContextProvider = ({ children }: PropTypes) => {
  const location = useLocation()
  const [{ data, fetching, error }] = useQuery({
    query: myFestivalsQuery,
    requestPolicy: 'network-only'
  })

  const festivals = useMemo(() => compact(data?.myFestivals) as Customer[], [data?.myFestivals])

  const { currentFestivalId } = useMyself() || {}
  const { selectFestival: executeSelectFestival } = useSelectFestival()
  const [pendingFestivalID, setPendingFestivalID] = useState<string | null>(null)

  const festival = useMemo(() => {
    return festivals.find(item => item.id === currentFestivalId) || null
  }, [currentFestivalId, festivals])

  // Der Festivalwechsel ist durch, sobald der Benutzer das neue Festival liefert
  useEffect(() => {
    if (pendingFestivalID && currentFestivalId === pendingFestivalID) {
      setPendingFestivalID(null)
    }
  }, [currentFestivalId, pendingFestivalID])

  const selectFestival = useCallback((id: string) => {
    if (id === currentFestivalId) {
      return
    }

    setPendingFestivalID(id)
    executeSelectFestival(id).then(success => {
      if (!success) {
        setPendingFestivalID(null)
      }
    })
  }, [currentFestivalId, executeSelectFestival])

  const isSelectionRoute = location.pathname === FESTIVAL_SELECTION_PATH

  // Mit genau einem Festival entfällt die Auswahl
  const autoSelectFestival = useMemo(() => {
    return !festival && !isSelectionRoute && !pendingFestivalID && festivals.length === 1 ? festivals[0] : null
  }, [festival, festivals, isSelectionRoute, pendingFestivalID])

  useEffect(() => {
    if (autoSelectFestival) {
      selectFestival(autoSelectFestival.id)
    }
  }, [autoSelectFestival, selectFestival])

  const value = useMemo(() => ({ festival, festivals, selectFestival }), [festival, festivals, selectFestival])

  if ((fetching || !error) && !data) {
    return <FestivalLoading />
  }

  if (autoSelectFestival || pendingFestivalID) {
    return <FestivalLoading />
  }

  return (
    <FestivalContextValue.Provider value={value}>
      {!festival || isSelectionRoute
        ? <FestivalSelection />
        : children
      }
    </FestivalContextValue.Provider>
  )
}

export default FestivalContextProvider
